import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Divider,
  Rating,
  Grid,
  Avatar,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Pagination
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import {
  Close,
  TrendingUp,
  Star,
  RateReview,
  AutoAwesome,
  Create,
  Visibility,
  AccessTime,
  AutoAwesome as AutoAwesomeIcon
} from '@mui/icons-material';
import EnhancedReviewsChart from './EnhancedReviewsChart';
import ApiService from '../../services/api';

import logger from '../../utils/logger';

const OverviewContainer = styled(Box)(({ theme }) => ({
  padding: '16px',
  width: '100%',
  [theme.breakpoints.up('sm')]: {
    padding: '20px'
  },
  [theme.breakpoints.up('md')]: {
    padding: '24px'
  }
}));

const TopSection = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: '1fr',
  gap: '16px',
  marginBottom: '20px',
  [theme.breakpoints.up('md')]: {
    gridTemplateColumns: '240px 1fr 280px',
    gap: '20px',
    marginBottom: '24px'
  },
  [theme.breakpoints.up('lg')]: {
    gridTemplateColumns: '280px 1fr 320px'
  }
}));

const RatingCard = styled(Card)(({ theme }) => ({
  borderRadius: '12px',
  background: 'linear-gradient(135deg, #1e3a5f 0%, #2d5a87 50%, #3b7ab8 100%)',
  color: 'white',
  padding: '20px 16px',
  textAlign: 'center',
  position: 'relative',
  overflow: 'hidden',
  boxShadow: '0 4px 16px rgba(30, 58, 95, 0.2)',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  WebkitTapHighlightColor: 'transparent',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: '-60px',
    right: '-40px',
    width: '120px',
    height: '120px',
    background: 'radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%)',
    borderRadius: '50%'
  },
  [theme.breakpoints.up('sm')]: {
    padding: '24px 20px',
    borderRadius: '14px'
  },
  [theme.breakpoints.up('md')]: {
    padding: '28px 24px',
    borderRadius: '16px',
    boxShadow: '0 8px 32px rgba(30, 58, 95, 0.25)',
    '&::before': {
      width: '150px',
      height: '150px'
    }
  }
}));

const RatingCircle = styled(Box)(({ theme }) => ({
  width: '75px',
  height: '75px',
  borderRadius: '50%',
  background: 'rgba(255, 255, 255, 0.15)',
  backdropFilter: 'blur(10px)',
  border: '3px solid rgba(255, 255, 255, 0.3)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginBottom: '10px',
  position: 'relative',
  zIndex: 1,
  [theme.breakpoints.up('md')]: {
    width: '90px',
    height: '90px',
    marginBottom: '12px'
  }
}));

const RatingNumber = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '26px',
  fontWeight: 700,
  color: 'white',
  lineHeight: 1,
  [theme.breakpoints.up('md')]: {
    fontSize: '32px'
  }
}));

const ChartCard = styled(Card)(({ theme }) => ({
  borderRadius: '12px',
  padding: '16px',
  background: '#ffffff',
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
  border: '1px solid #e5e7eb',
  [theme.breakpoints.up('sm')]: {
    padding: '18px',
    borderRadius: '14px'
  },
  [theme.breakpoints.up('md')]: {
    padding: '20px',
    borderRadius: '16px'
  }
}));

const StatsCard = styled(Card)(({ theme }) => ({
  borderRadius: '12px',
  padding: '16px',
  background: '#ffffff',
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
  border: '1px solid #e5e7eb',
  display: 'flex',
  flexDirection: 'column',
  [theme.breakpoints.up('sm')]: {
    padding: '18px',
    borderRadius: '14px'
  },
  [theme.breakpoints.up('md')]: {
    padding: '20px',
    borderRadius: '16px'
  }
}));

const StatItem = styled(Box)(({ theme, accentcolor }) => ({
  padding: '12px',
  borderRadius: '10px',
  background: '#f8fafc',
  border: '1px solid #e5e7eb',
  textAlign: 'center',
  position: 'relative',
  overflow: 'hidden',
  transition: 'all 0.2s ease',
  WebkitTapHighlightColor: 'transparent',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '3px',
    background: accentcolor || '#3b82f6'
  },
  '&:active': {
    transform: 'scale(0.98)'
  },
  [theme.breakpoints.up('md')]: {
    padding: '16px',
    borderRadius: '12px',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)'
    },
    '&:active': {
      transform: 'translateY(-1px)'
    }
  }
}));

const StatNumber = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '22px',
  fontWeight: 700,
  color: '#111827',
  lineHeight: 1,
  marginBottom: '4px',
  [theme.breakpoints.up('md')]: {
    fontSize: '28px'
  }
}));

const StatLabel = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '11px',
  fontWeight: 500,
  color: '#6b7280',
  [theme.breakpoints.up('md')]: {
    fontSize: '12px'
  }
}));

const ReviewsSection = styled(Card)(({ theme }) => ({
  borderRadius: '12px',
  padding: '16px',
  background: '#ffffff',
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
  border: '1px solid #e5e7eb',
  [theme.breakpoints.up('sm')]: {
    padding: '20px',
    borderRadius: '14px'
  },
  [theme.breakpoints.up('md')]: {
    padding: '24px',
    borderRadius: '16px'
  }
}));

const ReviewCard = styled(Box)(({ theme }) => ({
  padding: '12px',
  borderRadius: '10px',
  background: '#f8fafc',
  border: '1px solid #e5e7eb',
  transition: 'all 0.2s ease',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  WebkitTapHighlightColor: 'transparent',
  '&:active': {
    transform: 'scale(0.98)'
  },
  [theme.breakpoints.up('md')]: {
    padding: '16px',
    borderRadius: '12px',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: '0 8px 20px rgba(0, 0, 0, 0.08)',
      borderColor: '#d1d5db'
    },
    '&:active': {
      transform: 'translateY(-1px)'
    }
  }
}));

const ReviewHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginBottom: '10px',
  [theme.breakpoints.up('md')]: {
    marginBottom: '12px'
  }
}));

const AuthorInfo = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  [theme.breakpoints.up('md')]: {
    gap: '10px'
  }
}));

const AuthorAvatar = styled(Avatar)(({ theme }) => ({
  width: '32px',
  height: '32px',
  fontSize: '12px',
  fontWeight: 600,
  [theme.breakpoints.up('md')]: {
    width: '36px',
    height: '36px',
    fontSize: '14px'
  }
}));

const AuthorName = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '13px',
  fontWeight: 600,
  color: '#111827',
  [theme.breakpoints.up('md')]: {
    fontSize: '14px'
  }
}));

const ReviewDate = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '10px',
  color: '#9ca3af',
  [theme.breakpoints.up('md')]: {
    fontSize: '11px'
  }
}));

const ReviewText = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '12px',
  color: '#4b5563',
  lineHeight: 1.5,
  flex: 1,
  display: '-webkit-box',
  WebkitLineClamp: 3,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
  [theme.breakpoints.up('md')]: {
    fontSize: '13px',
    lineHeight: 1.6
  }
}));

const SectionHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginBottom: '12px',
  flexWrap: 'wrap',
  gap: '8px',
  [theme.breakpoints.up('md')]: {
    marginBottom: '16px'
  }
}));

const SectionTitle = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  [theme.breakpoints.up('md')]: {
    gap: '10px'
  }
}));

const TitleIcon = styled(Box)(({ theme, bgcolor }) => ({
  width: '32px',
  height: '32px',
  borderRadius: '8px',
  backgroundColor: bgcolor || '#eff6ff',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  [theme.breakpoints.up('md')]: {
    width: '36px',
    height: '36px',
    borderRadius: '10px'
  }
}));

const TitleText = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '14px',
  fontWeight: 600,
  color: '#111827',
  [theme.breakpoints.up('md')]: {
    fontSize: '16px'
  }
}));

const ActionButton = styled(Button)(({ theme, isprimary }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '12px',
  fontWeight: 600,
  textTransform: 'none',
  borderRadius: '8px',
  padding: '8px 14px',
  transition: 'all 0.2s ease',
  minHeight: '40px',
  background: isprimary === 'true' ? 'linear-gradient(135deg, #3b82f6 0%, #6366f1 100%)' : '#ffffff',
  color: isprimary === 'true' ? 'white' : '#374151',
  border: isprimary === 'true' ? 'none' : '1px solid #e5e7eb',
  boxShadow: isprimary === 'true' ? '0 2px 8px rgba(59, 130, 246, 0.3)' : 'none',
  WebkitTapHighlightColor: 'transparent',
  '&:active': {
    transform: 'scale(0.97)'
  },
  [theme.breakpoints.up('md')]: {
    fontSize: '13px',
    borderRadius: '10px',
    padding: '10px 18px',
    minHeight: '44px',
    '&:hover': {
      background: isprimary === 'true' ? 'linear-gradient(135deg, #2563eb 0%, #4f46e5 100%)' : '#f9fafb',
      transform: 'translateY(-1px)',
      boxShadow: isprimary === 'true' ? '0 4px 12px rgba(59, 130, 246, 0.4)' : '0 2px 8px rgba(0,0,0,0.08)'
    },
    '&:active': {
      transform: 'translateY(0)'
    }
  }
}));

const ViewAllButton = styled(Button)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '12px',
  fontWeight: 600,
  textTransform: 'none',
  color: '#3b82f6',
  padding: '6px 10px',
  borderRadius: '6px',
  minHeight: '36px',
  WebkitTapHighlightColor: 'transparent',
  '&:active': {
    backgroundColor: '#dbeafe'
  },
  [theme.breakpoints.up('md')]: {
    fontSize: '13px',
    padding: '6px 12px',
    borderRadius: '8px',
    '&:hover': {
      backgroundColor: '#eff6ff'
    }
  }
}));

const REVIEWS_PER_PAGE = 10;

const ReviewsOverviewModern = ({ reputationData }) => {
  const [reviewsOpen, setReviewsOpen] = useState(false);
  const [aiReplyDialogOpen, setAiReplyDialogOpen] = useState(false);
  const [aiReplySelectedTime, setAiReplySelectedTime] = useState('Immediately');
  const [aiReplySelectedRatings, setAiReplySelectedRatings] = useState(['5', '4', '3']);
  const [aiReplySaving, setAiReplySaving] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const navigate = useNavigate();

  // Use real data if available, otherwise fallback to mock data
  const data = reputationData || {
    yourBusiness: { rating: 4.8, reviewCount: 44 },
    reviews: []
  };

  // Handle new GMBService format - reviews might be in data.reviews.reviews
  const reviewsArray = Array.isArray(data.reviews) ? data.reviews : (data.reviews?.reviews || []);

  // Compute average rating once for star + number
  const avgRatingValue = reviewsArray.length > 0 ?
    Number((reviewsArray.reduce((sum, review) => {
      const rating = typeof review.starRating === 'string' ?
        ({ ONE: 1, TWO: 2, THREE: 3, FOUR: 4, FIVE: 5 }[review.starRating] || 0) :
        Number(review.starRating) || 0;
      return sum + rating;
    }, 0) / reviewsArray.length).toFixed(1)) :
    Number((Number(data.yourBusiness.rating) || 4.0).toFixed(1));

  // Use real reviews if available, otherwise use sample data
  const sortedReviews = Array.isArray(reviewsArray)
    ? [...reviewsArray].sort((a, b) => {
      const ta = a.createTime ? Date.parse(a.createTime) : 0;
      const tb = b.createTime ? Date.parse(b.createTime) : 0;
      return tb - ta; // newest first
    })
    : [];

  // Helper function to convert star rating to number
  const toNumericRating = (starRating) => {
    if (typeof starRating === 'number') return starRating;
    const map = { ONE: 1, TWO: 2, THREE: 3, FOUR: 4, FIVE: 5 };
    return map[String(starRating || '').toUpperCase()] || 5;
  };

  // All reviews for the dialog with pagination
  const allReviews = sortedReviews && sortedReviews.length > 0
    ? sortedReviews.map(review => ({
      author: review.reviewer?.displayName || 'Anonymous',
      rating: toNumericRating(review.starRating),
      text: review.comment || 'No comment provided',
      date: review.createTime ? new Date(review.createTime).toLocaleDateString() : 'Recent'
    }))
    : [
      {
        author: 'John D.',
        rating: 5,
        text: 'Great service and quick response. Highly recommended!',
        date: 'Recent'
      },
      {
        author: 'Priya S.',
        rating: 4,
        text: 'Good overall experience. Could improve waiting time.',
        date: 'Recent'
      },
      {
        author: 'Aman K.',
        rating: 3,
        text: 'Average. Got the job done but not very personable.',
        date: 'Recent'
      },
      {
        author: 'Sarah M.',
        rating: 5,
        text: 'Excellent customer service and professional staff. Will definitely return!',
        date: 'Recent'
      }
    ];

  // Display reviews for the preview cards (limited to 5)
  const displayReviews = allReviews.slice(0, 5);

  // Paginated reviews for the dialog
  const totalPages = Math.ceil(allReviews.length / REVIEWS_PER_PAGE);
  const paginatedReviews = allReviews.slice(
    (currentPage - 1) * REVIEWS_PER_PAGE,
    currentPage * REVIEWS_PER_PAGE
  );

  const handlePageChange = (event, page) => {
    setCurrentPage(page);
  };

  const handleOpenReviewsDialog = () => {
    setCurrentPage(1); // Reset to first page when opening
    setReviewsOpen(true);
  };

  // Calculate recent review stats with safe number handling
  const calculateRecentReviews = (reviews, days) => {
    if (!Array.isArray(reviews) || reviews.length === 0) {
      return days === 7 ? 2 : 8; // Default values
    }

    try {
      const cutoffDate = new Date();
      cutoffDate.setDate(cutoffDate.getDate() - days);

      return reviews.filter(r => {
        if (!r.createTime) return false;
        const reviewDate = new Date(r.createTime);
        return !isNaN(reviewDate.getTime()) && reviewDate >= cutoffDate;
      }).length;
    } catch (error) {
      logger.warn('Error calculating recent reviews:', error);
      return days === 7 ? 2 : 8;
    }
  };

  const recentReviews7Days = calculateRecentReviews(data.reviews, 7);
  const recentReviews30Days = calculateRecentReviews(data.reviews, 30);

  const getInitials = (name) => {
    return name.split(' ').map(n => n[0]).join('').toUpperCase();
  };

  const getAvatarColor = (name) => {
    const colors = ['#3b82f6', '#10b981', '#8b5cf6', '#f59e0b', '#ec4899'];
    const index = name.charCodeAt(0) % colors.length;
    return colors[index];
  };

  const handleAiReplyRatingToggle = (rating) => {
    setAiReplySelectedRatings(prev =>
      prev.includes(rating)
        ? prev.filter(r => r !== rating)
        : [...prev, rating]
    );
  };

  const handleAiReplySave = async () => {
    try {
      setAiReplySaving(true);

      const account = JSON.parse(localStorage.getItem('selectedGMBAccount') || 'null');
      const location = JSON.parse(localStorage.getItem('selectedGMBLocation') || 'null');

      if (!account?.name || !location?.name) {
        throw new Error('Please select a GMB account and location first');
      }

      await ApiService.post('/customer/auto-reply/settings', {
        accountName: account.name,
        locationName: location.name,
        locationId: location.name?.split('/').pop() || location.id,
        enabled: true,
        triggerTime: aiReplySelectedTime,
        ratings: aiReplySelectedRatings,
        tone: 'professional',
        businessName: localStorage.getItem('businessName') || account.accountName,
        businessCategory: localStorage.getItem('businessCategory') || 'business'
      });

      localStorage.setItem('autoReplySettings', JSON.stringify({
        enabled: true,
        triggerTime: aiReplySelectedTime,
        ratings: aiReplySelectedRatings
      }));

      setAiReplyDialogOpen(false);
      alert('AI Auto-Reply activated successfully! AI will automatically reply to new reviews based on your settings.');
    } catch (error) {
      logger.error('Error saving AI reply settings:', error);
      alert(error.message || 'Failed to save settings. Please try again.');
    } finally {
      setAiReplySaving(false);
    }
  };

  return (
    <OverviewContainer>
      <TopSection>
        {/* Rating Card */}
        <RatingCard>
          <Typography sx={{ fontSize: '12px', fontWeight: 500, color: 'rgba(255,255,255,0.7)', mb: 1, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            Average Rating
          </Typography>
          <RatingCircle>
            <RatingNumber>{avgRatingValue.toFixed(1)}</RatingNumber>
          </RatingCircle>
          <Rating
            value={avgRatingValue}
            precision={0.1}
            readOnly
            size="small"
            sx={{
              mb: 1,
              '& .MuiRating-iconFilled': { color: '#fbbf24' },
              '& .MuiRating-iconEmpty': { color: 'rgba(255, 255, 255, 0.3)' }
            }}
          />
          <Typography sx={{ fontSize: '13px', color: 'rgba(255,255,255,0.7)' }}>
            📝 {data.yourBusiness.reviewCount || 0} reviews
          </Typography>
        </RatingCard>

        {/* Chart Section */}
        <ChartCard>
          <SectionHeader>
            <SectionTitle>
              <TitleIcon bgcolor="#ecfdf5">
                <TrendingUp sx={{ color: '#10b981', fontSize: '18px' }} />
              </TitleIcon>
              <TitleText>Review Trends</TitleText>
            </SectionTitle>
          </SectionHeader>
          <EnhancedReviewsChart reputationData={reputationData} />
        </ChartCard>

        {/* Stats Section */}
        <StatsCard>
          <SectionHeader>
            <SectionTitle>
              <TitleIcon bgcolor="#fef3c7">
                <AccessTime sx={{ color: '#f59e0b', fontSize: '18px' }} />
              </TitleIcon>
              <TitleText>Recent Activity</TitleText>
            </SectionTitle>
          </SectionHeader>

          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', mb: 2 }}>
            <StatItem accentcolor="linear-gradient(90deg, #3b82f6, #6366f1)">
              <StatNumber>{recentReviews7Days}</StatNumber>
              <StatLabel>Last 7 Days</StatLabel>
            </StatItem>
            <StatItem accentcolor="linear-gradient(90deg, #10b981, #14b8a6)">
              <StatNumber>{recentReviews30Days}</StatNumber>
              <StatLabel>Last 30 Days</StatLabel>
            </StatItem>
          </Box>

          {/* Activate AI Review Reply Button */}
          <Button
            onClick={() => setAiReplyDialogOpen(true)}
            startIcon={<AutoAwesomeIcon />}
            data-tutorial="ai-review-reply"
            sx={{
              mb: 2,
              width: '100%',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: '#ffffff',
              fontFamily: 'Inter, sans-serif',
              fontSize: '14px',
              fontWeight: 600,
              textTransform: 'none',
              borderRadius: '12px',
              padding: '12px 16px',
              boxShadow: '0 4px 15px rgba(102, 126, 234, 0.4)',
              transition: 'all 0.3s ease',
              '&:hover': {
                background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
                boxShadow: '0 6px 20px rgba(102, 126, 234, 0.5)',
                transform: 'translateY(-2px)'
              }
            }}
          >
            Activate AI Review Reply
          </Button>

          <Box sx={{ display: 'flex', gap: '10px', mt: 'auto' }}>
            <ActionButton
              isprimary="true"
              startIcon={<AutoAwesome sx={{ fontSize: '16px' }} />}
              onClick={() => navigate('/reviews-management')}
              fullWidth
              data-tutorial="reputation-ai-reply"
            >
              AI Reply
            </ActionButton>
            <ActionButton
              isprimary="false"
              startIcon={<Create sx={{ fontSize: '16px' }} />}
              onClick={() => navigate('/create-post')}
              fullWidth
              data-tutorial="create-testimonial-post"
            >
              Create Post
            </ActionButton>
          </Box>
        </StatsCard>
      </TopSection>

      {/* Recent Reviews */}
      <ReviewsSection>
        <SectionHeader>
          <SectionTitle>
            <TitleIcon bgcolor="#eff6ff">
              <RateReview sx={{ color: '#3b82f6', fontSize: '18px' }} />
            </TitleIcon>
            <TitleText>Recent Reviews</TitleText>
          </SectionTitle>
          <ViewAllButton
            onClick={handleOpenReviewsDialog}
            endIcon={<Visibility sx={{ fontSize: '16px' }} />}
          >
            View All ({allReviews.length})
          </ViewAllButton>
        </SectionHeader>

        <Grid container spacing={2}>
          {displayReviews.slice(0, 4).map((review, idx) => (
            <Grid item xs={12} sm={6} md={3} key={idx}>
              <ReviewCard>
                <ReviewHeader>
                  <AuthorInfo>
                    <AuthorAvatar sx={{ bgcolor: getAvatarColor(review.author) }}>
                      {getInitials(review.author)}
                    </AuthorAvatar>
                    <Box>
                      <AuthorName>{review.author}</AuthorName>
                      <ReviewDate>{review.date}</ReviewDate>
                    </Box>
                  </AuthorInfo>
                </ReviewHeader>
                <Rating
                  value={review.rating}
                  size="small"
                  readOnly
                  sx={{
                    mb: 1,
                    '& .MuiRating-iconFilled': { color: '#fbbf24' },
                    '& .MuiRating-iconEmpty': { color: '#e5e7eb' }
                  }}
                />
                <ReviewText>{review.text}</ReviewText>
              </ReviewCard>
            </Grid>
          ))}
        </Grid>
      </ReviewsSection>

      {/* Reviews Modal */}
      <Dialog
        open={reviewsOpen}
        onClose={() => setReviewsOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '16px',
            maxHeight: '80vh'
          }
        }}
      >
        <DialogTitle sx={{ p: 3, pb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <TitleIcon bgcolor="#eff6ff">
                <RateReview sx={{ color: '#3b82f6', fontSize: '18px' }} />
              </TitleIcon>
              <TitleText>All Reviews</TitleText>
            </Box>
            <IconButton
              onClick={() => setReviewsOpen(false)}
              sx={{
                borderRadius: '10px',
                '&:hover': { backgroundColor: '#f3f4f6' }
              }}
            >
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 3, pt: 0 }}>
          <Typography variant="body2" sx={{ mb: 2, color: '#6b7280', fontFamily: 'Inter, sans-serif' }}>
            Showing {((currentPage - 1) * REVIEWS_PER_PAGE) + 1}-{Math.min(currentPage * REVIEWS_PER_PAGE, allReviews.length)} of {allReviews.length} reviews
          </Typography>
          <Stack spacing={2}>
            {paginatedReviews.map((review, idx) => (
              <Box key={idx}>
                <ReviewCard>
                  <ReviewHeader>
                    <AuthorInfo>
                      <AuthorAvatar sx={{ bgcolor: getAvatarColor(review.author) }}>
                        {getInitials(review.author)}
                      </AuthorAvatar>
                      <Box>
                        <AuthorName>{review.author}</AuthorName>
                        <ReviewDate>{review.date}</ReviewDate>
                      </Box>
                    </AuthorInfo>
                    <Rating
                      value={review.rating}
                      size="small"
                      readOnly
                      sx={{
                        '& .MuiRating-iconFilled': { color: '#fbbf24' },
                        '& .MuiRating-iconEmpty': { color: '#e5e7eb' }
                      }}
                    />
                  </ReviewHeader>
                  <ReviewText sx={{ WebkitLineClamp: 'unset' }}>
                    {review.text}
                  </ReviewText>
                </ReviewCard>
                {idx < paginatedReviews.length - 1 && <Divider sx={{ mt: 1 }} />}
              </Box>
            ))}
          </Stack>
          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
              <Pagination
                count={totalPages}
                page={currentPage}
                onChange={handlePageChange}
                color="primary"
                shape="rounded"
                sx={{
                  '& .MuiPaginationItem-root': {
                    fontFamily: 'Inter, sans-serif',
                    fontWeight: 500
                  }
                }}
              />
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* AI Auto-Reply Dialog */}
      <Dialog open={aiReplyDialogOpen} onClose={() => setAiReplyDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{
          fontFamily: 'Inter, sans-serif',
          fontSize: '20px',
          fontWeight: 600,
          color: '#0B91D6',
          borderBottom: '1px solid #e5e7eb',
          pb: 2
        }}>
          Activate AI Review Reply
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Stack spacing={3}>
            {/* Trigger Time Selection */}
            <div data-tutorial="ai-reply-time" style={{ width: '100%' }}>
              <FormControl fullWidth>
                <InputLabel sx={{ fontFamily: 'Inter, sans-serif' }}>When to Reply</InputLabel>
                <Select
                  value={aiReplySelectedTime}
                  onChange={(e) => setAiReplySelectedTime(e.target.value)}
                  label="When to Reply"
                  sx={{ fontFamily: 'Inter, sans-serif' }}
                >
                  <MenuItem value="Immediately">Immediately</MenuItem>
                  <MenuItem value="After 1 hour">After 1 hour</MenuItem>
                  <MenuItem value="After 2 hours">After 2 hours</MenuItem>
                  <MenuItem value="After 4 hours">After 4 hours</MenuItem>
                  <MenuItem value="After 24 hours">After 24 hours</MenuItem>
                </Select>
              </FormControl>
            </div>

            {/* Rating Selection */}
            <Box>
              <Typography variant="body2" sx={{
                fontFamily: 'Inter, sans-serif',
                fontWeight: 500,
                mb: 2,
                color: '#374151'
              }}>
                Select Ratings to Auto-Reply To
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
                {['5', '4', '3', '2', '1'].map((rating) => (
                  <Chip
                    key={rating}
                    label={`${rating} Star${rating !== '1' ? 's' : ''}`}
                    onClick={() => handleAiReplyRatingToggle(rating)}
                    color={aiReplySelectedRatings.includes(rating) ? 'primary' : 'default'}
                    variant={aiReplySelectedRatings.includes(rating) ? 'filled' : 'outlined'}
                    sx={{
                      fontFamily: 'Inter, sans-serif',
                      fontSize: '14px',
                      cursor: 'pointer',
                      '&:hover': {
                        backgroundColor: aiReplySelectedRatings.includes(rating)
                          ? 'rgba(11, 145, 214, 0.8)'
                          : 'rgba(11, 145, 214, 0.1)'
                      }
                    }}
                  />
                ))}
              </Stack>
            </Box>

            {/* Info Text */}
            <Box sx={{
              backgroundColor: '#f3f4f6',
              p: 2,
              borderRadius: '8px',
              border: '1px solid #e5e7eb'
            }}>
              <Typography variant="body2" sx={{
                fontFamily: 'Inter, sans-serif',
                color: '#6b7280',
                fontSize: '13px',
                lineHeight: '18px'
              }}>
                <strong>Note:</strong> AI will automatically generate appropriate replies based on the review content and rating.
                Replies will be sent to Google reviews only.
              </Typography>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 2 }}>
          <Button
            onClick={() => setAiReplyDialogOpen(false)}
            sx={{
              fontFamily: 'Inter, sans-serif',
              textTransform: 'none',
              color: '#6b7280'
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleAiReplySave}
            variant="contained"
            disabled={aiReplySaving || aiReplySelectedRatings.length === 0}
            data-tutorial="set-ai-reply"
            sx={{
              fontFamily: 'Inter, sans-serif',
              textTransform: 'none',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              '&:hover': { background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)' },
              '&:disabled': { backgroundColor: '#d1d5db' }
            }}
          >
            {aiReplySaving ? 'Activating...' : 'Set AI Auto Reply'}
          </Button>
        </DialogActions>
      </Dialog>
    </OverviewContainer>
  );
};

export default ReviewsOverviewModern;
